import { Component, OnInit, signal, computed, effect, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Header } from "../../header/header";
import { Carrusel } from "../../carrusel/carrusel";
import { Footer } from "../../footer/footer";
import { ServiceService } from '../../services/service.serivece';
import { WebView } from "../../components/web-view/web-view";

// Type definitions for better type safety
interface Service {
  id: number;
  name: string;
  description: string;
  icon: string;
  url: string;
  type: 'LAUNCH-WEB' | 'WEB' | 'DEVELOPERS' | string;
}

interface ServiceResponse {
  data: {
    services: Service[];
  };
}

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [Header, Carrusel, Footer, WebView, CommonModule],
  templateUrl: './home.html',
  styleUrl: './home.css'
})
export class Home implements OnInit {
  // Dependency injection using inject() (Angular 14+)
  private readonly router = inject(Router);
  private readonly serviceService = inject(ServiceService);
  private readonly destroyRef = inject(DestroyRef);

  // Signals for reactive state management (Angular 16+)
  public gsid = signal<number>(0);
  public services = signal<Service[]>([]);
  public isIberoView = signal<boolean>(false);
  public iberoViewUrl = signal<string>('');
  public email = signal<string>('');
  public isLoading = signal<boolean>(false);
  public error = signal<string | null>(null);

  // Computed signals for derived state
  public hasServices = computed(() => this.services().length > 0);
  public isReady = computed(() => !this.isLoading() && this.hasServices());
  public filteredServices = computed(() => {
    // You can add filtering logic here if needed
    return this.services();
  });

  constructor() {
    // Effect to monitor service changes (for debugging/analytics)
    effect(() => {
      const serviceCount = this.services().length;
      if (serviceCount > 0) {
        console.log(`Loaded ${serviceCount} services`);
      }
    }, { allowSignalWrites: false });

    // Initialize email from localStorage
    this.initializeUserData();
  }

  ngOnInit(): void {
    this.initializeComponent();
  }

  private initializeUserData(): void {
    try {
      const storedEmail = localStorage.getItem('gsemail');
      if (storedEmail) {
        this.email.set(storedEmail);
      }
    } catch (error) {
      console.error('Error reading user data from localStorage:', error);
    }
  }

  private initializeComponent(): void {
    const storedGsid = Number(localStorage.getItem('gsid') || '0');
    
    if (storedGsid > 0) {
      this.gsid.set(storedGsid);
      this.getServices(storedGsid);
    } else {
      this.error.set('GSID no válido');
      console.error('Invalid GSID from localStorage');
    }
  }

  private getServices(gsid: number): void {
    console.log('3 ==> Fetching services for GSID:', gsid);
    this.isLoading.set(true);
    this.error.set(null);

    this.serviceService.getServices(gsid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: ServiceResponse) => {
          this.isLoading.set(false);
          
          if (response?.data?.services && Array.isArray(response.data.services)) {
            this.services.set(response.data.services);
            console.log(`Successfully loaded ${response.data.services.length} services`);
          } else {
            this.error.set('No se encontraron servicios');
            console.warn('Invalid service response structure:', response);
          }
        },
        error: (error) => {
          console.error('Error fetching services:', error);
          this.isLoading.set(false);
          this.error.set('Error al cargar los servicios. Por favor, intenta de nuevo.');
        }
      });
  }

  public retryLoadServices(): void {
    const currentGsid = this.gsid();
    if (currentGsid > 0) {
      this.getServices(currentGsid);
    }
  }

  public handleNavigation(service: Service, emailIbero?: string): void {
    if (!service?.url) {
      console.error('Service URL is required');
      this.error.set('URL del servicio no disponible');
      return;
    }

    try {
      switch (service.type) {
        case 'LAUNCH-WEB':
        case 'WEB':
          this.openExternalUrl(service.url);
          break;
        
        case 'DEVELOPERS':
          this.handleDeveloperService(service, emailIbero);
          break;
        
        default:
          console.warn(`Unknown service type: ${service.type}`);
          this.error.set(`Tipo de servicio no soportado: ${service.type}`);
      }
    } catch (error) {
      console.error('Error navigating to service:', error);
      this.error.set('Error al navegar al servicio');
    }
  }

  private openExternalUrl(url: string): void {
    try {
      // Intentar abrir la URL en una nueva pestaña
      window.open(url, '_blank', 'noopener,noreferrer');
      
      // No mostrar error automáticamente ya que window.open() puede retornar null
      // por políticas de seguridad del navegador incluso cuando se abre correctamente.
      // El usuario verá si la pestaña se abre o no.
      
    } catch (error) {
      console.error('Error opening external URL:', error);
      this.error.set('No se pudo abrir la ventana. Por favor, verifica los bloqueadores de pop-ups.');
    }
  }

  private handleDeveloperService(service: Service, emailIbero?: string): void {
    // Always open developer services in WebView
    const email = emailIbero || this.email();
    const fullUrl = `${service.url}${email}`;
    
    console.log('Opening in WebView:', fullUrl);
    
    this.iberoViewUrl.set(fullUrl);
    this.isIberoView.set(true);
  }

  public closeIframe(event?: Event): void {
    this.isIberoView.set(false);
    this.iberoViewUrl.set('');
  }

  public clearError(): void {
    this.error.set(null);
  }

  // Utility method to refresh services
  public refreshServices(): void {
    const currentGsid = this.gsid();
    if (currentGsid > 0) {
      this.getServices(currentGsid);
    }
  }
}
